import type { SocialConnectionObject } from '@bvengineering/cas-member-gateway-service-client';
import { useApiQuery } from '@/api/hooks/useQuery';
import { socialConnectionsKeys } from '@/api/queryKeys';
import type { SocialProvider } from './constants';

export type { SocialProvider };

/**
 * Hook to fetch the current user's social connections.
 * This is the proper way to check if Instagram/TikTok/etc. is connected.
 */
export function useSocialConnections() {
  return useApiQuery({
    queryKey: socialConnectionsKeys.list(),
    queryFn: (api) => async () => {
      const response = await api.settings.settingsControllerListSocialConnections();
      return response.data;
    },
  });
}

/**
 * Helper to check if a specific provider is connected.
 * Returns true if the user has a connection for the given service.
 *
 * This is a derived query (not a direct API call) — it wraps useSocialConnections.
 */
export function useIsProviderConnected(provider: SocialProvider) {
  const { data: connections, ...rest } = useSocialConnections();

  // Check if any connection matches the provider
  const isConnected =
    connections?.results?.some((conn: SocialConnectionObject) => conn.service === provider) ??
    false;

  // Get the username if connected
  const connection = connections?.results?.find(
    (conn: SocialConnectionObject) => conn.service === provider
  );
  const username = connection?.serviceUserName;

  return { isConnected, username, ...rest };
}
